// React hooks
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// Helpers i API servisi
import { deleteContactPhoneAPI, getContactAPI, getContactPhonesAPI } from "services/api/contacts/api";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import {
  ContactPhoneSearchPageCmp,
  ContactPhoneSearchPageProps,
} from "./types";
import { CONTACT_PHONE_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";

// Moduli specifični za projekt
import {
  Routes,
} from "ui/modules/contacts/navigation";
import TabMenu from "ui/modules/contacts/pages/components/TabMenu/TabMenu";

// Lokalne komponente
import ContactPhoneSearchResults from "./components/ContactPhoneSearchResults/ContactPhoneSearchResults";
import ContactPhoneSearchFilters from "./components/ContactPhoneSearchFilters/ContactPhoneSearchFilters";

import ContactPhoneEditCard from "../ContactPhoneEditCard/ContactPhoneEditCard";
import { ContactDetailFormValues, ContactPhoneRow, ContactPhoneSearchFormValues } from "services/api/contacts/types";
import { useTranslate } from "helpers";
import { Dialog } from "ui/components/Dialog";
import { initialContactDetailForm } from "services/api/contacts/consts";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { SIDEBAR_ITEM_CONTACT } from "app/components/Sidebar/consts";

const ContactPhoneSearchPage: ContactPhoneSearchPageCmp = (
  props: ContactPhoneSearchPageProps
) => {
  const { id } = useParams();
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<ContactPhoneRow>
  >(initialDataWithPagination);

  const [formValues, setFormValues] = useState<ContactDetailFormValues>(
    initialContactDetailForm
  );

  const [phoneId, setPhoneId] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (id) {
      fetchSearchResult(searchResults.pagination, id);
      fetchContact(id);
    }
  }, [id]);

  const fetchContact = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialContactDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getContactAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);
  };

  const fetchSearchResult = async (pagination: Pagination, contactId: string, name?: string | null) => {
    setIsLoading(true);
    try {
      if (id) {
        const results = await getContactPhonesAPI(pagination, contactId, name);

        setSearchResults(results);
      }
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setPhoneId(id);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setPhoneId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback((searchFormValues: ContactPhoneSearchFormValues) => {
    if (id)
      fetchSearchResult(searchResults.pagination, id, searchFormValues.search);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (id)
      fetchSearchResult(newPagination, id);
  };

  const handleEditSuccess = useCallback(() => {
    if (id)
      fetchSearchResult(searchResults.pagination, id);
    setShowAddNewModal(false);
  }, []);

  const handlePhoneClick = useCallback((event: React.MouseEvent<HTMLSpanElement>, phone: string) => {
    event.stopPropagation();
    event.preventDefault();
    window.location.href = `tel:${phone}`;
  }, []);

  const handleWhatsAppClick = useCallback((event: React.MouseEvent<HTMLSpanElement>, phone: string) => {
    event.stopPropagation();
    event.preventDefault();
    const cleanPhone = phone.replace(/\D/g, '');
    // WhatsApp uses international format without +
    const internationalPhone = cleanPhone.startsWith('0')
      ? '385' + cleanPhone.substring(1)
      : cleanPhone;
    window.open(`https://wa.me/${internationalPhone}`, '_blank', 'noopener,noreferrer');
  }, []);

  const handleViberClick = useCallback((event: React.MouseEvent<HTMLSpanElement>, phone: string) => {
    event.stopPropagation();
    event.preventDefault();
    const cleanPhone = phone.replace(/\D/g, '');
    // Viber uses international format with +
    const internationalPhone = cleanPhone.startsWith('0')
      ? '+385' + cleanPhone.substring(1)
      : '+' + cleanPhone;
    window.open(`viber://chat?number=${encodeURIComponent(internationalPhone)}`, '_blank');
  }, []);

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setPhoneId(id);
    setOpenDialog(true);
  }
  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!phoneId) return;

    try {
      if (id) {
        const deleted = await deleteContactPhoneAPI(
          t,
          id,
          phoneId,
        );

        if (deleted) {
          await fetchSearchResult(searchResults.pagination, id);
        }
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setPhoneId("");
    } catch (err) {
      console.error("Greška prilikom brisanja phone-a:", err);
    }
  }, [phoneId, t, id, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setPhoneId("");
    setOpenDialog(false);
  }, []);

  const breadcrumbs: BreadcrumbItem[] = [
    { text: SIDEBAR_ITEM_CONTACT.text, route: SIDEBAR_ITEM_CONTACT.route },
  ];


  if (!id) return <>REDIRECT TODO</>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">

          <PageHeader>
            <div className="flex justify-between">
              <div className="flex flex-col gap-2">
                <div className="flex gap-3">
                  {formValues.pictures && formValues.pictures.length > 0 && (
                    <img src={formValues.pictures[0].url} alt={formValues.companyName ?? ""} title={formValues.companyName ?? ""} className="h-12 w-12 object-contain rounded-full" />
                  )}
                  <div>
                    <PageTitle
                      title={`${t(CONTACT_PHONE_SEARCH_PAGE_TITLE.text)} [${formValues.companyName || `${formValues.firstName} ${formValues.lastName}`}]`}
                      icon={CONTACT_PHONE_SEARCH_PAGE_TITLE.icon}
                      iconColor={CONTACT_PHONE_SEARCH_PAGE_TITLE.iconColor}
                    />
                    <Breadcrumb items={breadcrumbs} />
                  </div>
                </div>
              </div>
              <div className="flex gap-2 items-center">
              </div>
            </div>
          </PageHeader>
          <TabMenu contactId={id} contactTypeId={formValues?.contactType?.id} />
        </div>
        <div className="px-4 py-4">
          <ContactPhoneSearchFilters
            onAddNew={handleAddNew}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Unesite telefon klikom na 'Dodaj telefon'" />
          ) : (
            <ContactPhoneSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onPhoneClick={handlePhoneClick}
              onDeleteClick={handleDeleteClick}
              onWhatsAppClick={handleWhatsAppClick}
              onViberClick={handleViberClick}
            />
          )}
        </div>
      </div>
      <ContactPhoneEditCard
        phoneId={phoneId}
        contactId={id}
        show={showAddNewModal}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
      <Dialog
        open={openDialog}
        title={t(
          "admin.contact.phone.dialog.delete.title"
        )}
        message={t(
          "admin.contact.phone.dialog.delete.message"
        )}
        confirmText={t(
          "admin.contact.phone.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.contact.phone.dialog.delete.canceltext"
        )}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
};

ContactPhoneSearchPage.route = Routes.ContactPhoneSearchPageRouter;

export default ContactPhoneSearchPage;
